import React, { useState } from 'react';
import { CompanyTrendChart } from '../components/admin/CompanyTrendChart';
import { GroupFilter, PeopleTable } from '../components/admin/PeopleTable';
import { PositionBreakdown } from '../components/admin/PositionBreakdown';
import { PeriodPicker, PeriodStatusChip } from '../components/PeriodPicker';
import { StatCard } from '../components/StatCard';
import { useCommissionData } from '../contexts/CommissionDataContext';
import { periods } from '../data/commissions';
import { officers } from '../data/officers';
import { useCompanyOverview } from '../hooks/useCompanyOverview';
import { formatCurrency } from '../utils/commission';
function trendFor(current: number, prior: number | undefined, priorLabel: string | undefined) {
  if (prior === undefined || !priorLabel) return undefined;
  if (prior === 0) return {
    label: 'New',
    direction: 'up' as const
  };
  const change = (current - prior) / prior;
  const percent = Math.round(change * 100);
  return {
    label: `${percent > 0 ? '+' : ''}${percent}%`,
    direction: percent > 0 ? 'up' as const : percent < 0 ? 'down' as const : 'flat' as const
  };
}
export function AdminOverview() {
  const {
    setPeriodId
  } = useCommissionData();
  const {
    period,
    prior,
    rows,
    current,
    priorSummary,
    trend,
    groups
  } = useCompanyOverview();
  const [group, setGroup] = useState<GroupFilter>('All');
  const vsLabel = prior ? `vs ${prior.label}${period.isCurrent ? ' (month to date)' : ''}` : undefined;
  function selectGroup(next: GroupFilter) {
    setGroup(value => value === next ? 'All' : next);
    document.getElementById('people')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
  return <>
      <header className="border-b border-slate-200 bg-white" data-id="element-555" data-mp="v18b2yo8nygu">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-556" data-mp="uyds7bkvilpj">
          <div data-id="element-557" data-mp="2wrtha41bbsie">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-700" data-id="element-558" data-mp="2wrbeeok5ag1i">Admin</p>
            <h1 className="mt-0.5 text-2xl font-bold text-slate-900" data-id="element-559" data-mp="3wtbxqytf1au">Company commissions</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500" data-id="element-560" data-mp="3v4fj3tkqtlwg">
              {period.label}
              <PeriodStatusChip status={period.status} data-id="element-561" data-mp="czc9pwnqunq8" />
              {period.isCurrent ? <span data-id="element-562" data-mp="8vmptki0kubj">
                  Day {period.daysElapsed} of {period.daysTotal}
                </span> : null}
            </p>
          </div>
          <PeriodPicker periods={periods} selectedPeriodId={period.id} onSelect={setPeriodId} data-id="element-563" data-mp="hxducm0tiv3t" />
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-6 py-8" data-id="element-564" data-mp="wmjqu289u780">
        <section aria-label="Company summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-565" data-mp="wo389w5nyobd">
          <StatCard label={period.isCurrent ? 'Commissions to date' : 'Total commissions'} value={formatCurrency(current.commission)} helper={vsLabel} trend={trendFor(current.commission, priorSummary?.commission, prior?.label)} emphasis data-id="element-566" data-mp="rpjnw06pxkl4" />
          <StatCard label="Funded units" value={String(current.fundedUnits)} helper={vsLabel} trend={trendFor(current.fundedUnits, priorSummary?.fundedUnits, prior?.label)} data-id="element-567" data-mp="1p8qlr2abqvwq" />
          <StatCard label="Funded volume" value={formatCurrency(current.fundedVolume)} helper={vsLabel} trend={trendFor(current.fundedVolume, priorSummary?.fundedVolume, prior?.label)} data-id="element-568" data-mp="1f9j6uanxyt7i" />
          <StatCard label="People earning" value={`${current.paidCount} of ${officers.length}`} helper={current.paidCount > 0 ? `${formatCurrency(current.commission / current.paidCount)} average` : 'No commissions yet'} data-id="element-569" data-mp="16bz04m7b84pi" />
        </section>

        <section className="grid gap-6 lg:grid-cols-3" data-id="element-570" data-mp="njo5tgqq1f30">
          <div className="lg:col-span-2" data-id="element-571" data-mp="nl7kj2wqbi2o">
            <CompanyTrendChart data={trend} data-id="element-572" data-mp="fiypji9ix7ce" />
          </div>
          <PositionBreakdown groups={groups} activeGroup={group} onSelectGroup={selectGroup} data-id="element-573" data-mp="ajm0qrz3dq76" />
        </section>

        <PeopleTable rows={rows} group={group} onGroupChange={setGroup} periodLabel={period.label} data-id="element-574" data-mp="5snk1ra6yq3e" />
      </main>
    </>;
}